import { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { IRequestApiCallStatus, IRequestState } from '.';
import { useAlertContext, ErrorIcon, SuccessIcon } from '../common';
import { useSubmitRequestFlow } from '../login';
import { Either, left, map as emap, right } from 'fp-ts/lib/Either';
import { compose as lcompose, find as lfind, getOr as lgetOr } from 'lodash/fp';

export function useEmploymentVerificationView({
  requestState,
}: {
  requestState: IRequestState;
}) {
  let history = useNavigate();

  const submitRequestFlow = useSubmitRequestFlow();

  const { setAlert } = useAlertContext();

  useEffect(() => {
    lcompose(
      emap(() =>
        setAlert(a => ({
          ...a,
          title: 'CSOS Submit Request',
          message: () => (
            <p>
              <FontAwesomeIcon icon={faSpinner} size="1x" fixedWidth spin />
              submitting request
            </p>
          ),
          okAction: false,
          cancelAction: false,
          isOpen: true,
        }))
      ),
      getRequest(submitRequestFlow)
    )(requestState);
  }, [submitRequestFlow, requestState, setAlert]);

  useEffect(() => {
    lcompose(
      emap(() =>
        setAlert(a => ({
          ...a,
          title: 'CSOS Submit Request',
          message: () => (
            <p>
              <ErrorIcon />
              {lcompose(
                lgetOr(
                  'Error occured when saving or submitting request.',
                  'error.message'
                )
              )(requestState)}
            </p>
          ),
          okAction: true,
          cancelAction: false,
          isOpen: true,
        }))
      ),
      getError(submitRequestFlow)
    )(requestState);
  }, [submitRequestFlow, requestState, setAlert]);

  useEffect(() => {
    lcompose(
      emap(() =>
        setAlert(a => ({
          ...a,
          title: 'CSOS Submit Request',
          message: () => (
            <p>
              <SuccessIcon />
              success
            </p>
          ),
          okAction: true,
          cancelAction: false,
          isOpen: true,
          onOk: () => history('/my-information'),
        }))
      ),
      getSuccess(submitRequestFlow)
    )(requestState);
  }, [submitRequestFlow, requestState, setAlert, history]);
}

const getRequest: (
  submitRequestFlow: boolean
) => (requestState: IRequestState) => Either<undefined, undefined> = (
  submitRequestFlow: boolean
) =>
  lcompose(
    a => (a === void 0 || !submitRequestFlow ? left(void 0) : right(void 0)),
    lfind<IRequestApiCallStatus>({
      type: 'SaveRequest',
      apiCallStatus: null,
    }),
    lgetOr([], 'apiCallStatus')
  );

const getError: (
  submitRequestFlow: boolean
) => (requestState: IRequestState) => Either<undefined, undefined> = (
  submitRequestFlow: boolean
) =>
  lcompose(
    a => (a === void 0 || !submitRequestFlow ? left(void 0) : right(void 0)),
    lfind<IRequestApiCallStatus>({
      type: 'SaveRequest',
      apiCallStatus: false,
    }),
    lgetOr([], 'apiCallStatus')
  );

const getSuccess: (
  submitRequestFlow: boolean
) => (requestState: IRequestState) => Either<undefined, undefined> = (
  submitRequestFlow: boolean
) =>
  lcompose(
    a => (a === void 0 || !submitRequestFlow ? left(void 0) : right(void 0)),
    lfind<IRequestApiCallStatus>({
      type: 'SaveRequest',
      apiCallStatus: true,
    }),
    lgetOr([], 'apiCallStatus')
  );
